import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getGroupAiNotesConfig,
  putGroupAiNotesConfig,
  deleteGroupAiNotesConfig,
  getGroupAiNotesUsage,
  getGroupAiNotesCalls,
  getExpedientAiNotes,
  getAiNote,
  postAiNoteAccept,
  postAiNoteDiscard,
} from 'services/api';
import { AI_NOTE_ALREADY_RESOLVED, AI_NOTE_NOT_FOUND } from './aiNotesUtils';
import { AiNotesState, GroupAiNotesConfigBody } from './types';

// Las mutaciones devuelven solo `data` (la respuesta de axios entera no es serializable y Redux avisa en dev).

// --- Config por grupo (pestaña «Notas IA») ---

export const getGroupAiNotesConfigAsync: any = createAsyncThunk('aiNotes/getGroupConfig', async (groupId: string) => {
  const response = await getGroupAiNotesConfig(groupId);
  return response?.data?.item ?? null;
});

export const putGroupAiNotesConfigAsync: any = createAsyncThunk(
  'aiNotes/putGroupConfig',
  async (data: { groupId: string; config: GroupAiNotesConfigBody }, { dispatch }) => {
    const response = await putGroupAiNotesConfig(data.groupId, data.config);
    if (response?.data?.success) {
      // await: el componente limpia su dirty contra el estado NUEVO (mismo criterio que FHIR)
      await dispatch(getGroupAiNotesConfigAsync(data.groupId));
    }
    return { data: response?.data };
  },
);

export const deleteGroupAiNotesConfigAsync: any = createAsyncThunk(
  'aiNotes/deleteGroupConfig',
  async (groupId: string, { dispatch }) => {
    const response = await deleteGroupAiNotesConfig(groupId);
    if (response?.data?.success) {
      await dispatch(getGroupAiNotesConfigAsync(groupId));
    }
    return { data: response?.data };
  },
);

// --- Consumo ---

export const getGroupAiNotesUsageAsync: any = createAsyncThunk(
  'aiNotes/getGroupUsage',
  async (data: { groupId: string; from: string; to: string }) => {
    const response = await getGroupAiNotesUsage(data.groupId, data.from, data.to);
    return response?.data?.item ?? null;
  },
);

export const getGroupAiNotesCallsAsync: any = createAsyncThunk(
  'aiNotes/getGroupCalls',
  async (data: { groupId: string; month: string; page: number; size: number }) => {
    const response = await getGroupAiNotesCalls(data.groupId, data.month, data.page, data.size);
    // gdp envuelve MultipleResults como item.items (como pacientes, profesionales…)
    return response?.data?.item?.items ?? response?.data?.results ?? [];
  },
);

// --- Notas IA del operador ---

export const getExpedientAiNotesAsync: any = createAsyncThunk(
  'aiNotes/getExpedientNotes',
  async (expedientId: string) => {
    const response = await getExpedientAiNotes(expedientId);
    // gdp envuelve MultipleResults como item.items (como pacientes, profesionales…)
    return response?.data?.item?.items ?? response?.data?.results ?? [];
  },
);

// Sondeo de la llamada seguida (AiNoteLiveTracker): silenciado en errorNotificationMiddleware. El
// reducer descarta la respuesta si ya no es la llamada seguida.
export const getAiNoteLiveAsync: any = createAsyncThunk('aiNotes/getAiNoteLive', async (eventId: string) => {
  const response = await getAiNote(eventId);
  return response?.data?.item ?? null;
});

/**
 * Tras aceptar/descartar (o si el back dice que ya estaba resuelta o que no existe) se repide lo que
 * pinta esa nota: la nota en vivo SOLO si es la llamada que sigue la atención (si no, se pisaría la
 * de otra llamada) y la lista de cada expediente que la tenga cargada, empezando por el de la propia
 * nota (aceptar crea la NOTES en ese expediente, no en el de la atención).
 */
const refreshResolvedNote = (dispatch: any, getState: () => unknown, eventId: string, expedientId?: string | null) => {
  const { aiNotes } = getState() as { aiNotes: AiNotesState };
  if (aiNotes.trackedEventId === eventId) dispatch(getAiNoteLiveAsync(eventId));
  const expedientIds = new Set<string>();
  if (expedientId) expedientIds.add(expedientId);
  Object.keys(aiNotes.byExpedientId).forEach((id) => {
    if (aiNotes.byExpedientId[id].results.some((note) => note.eventId === eventId)) expedientIds.add(id);
  });
  expedientIds.forEach((id) => dispatch(getExpedientAiNotesAsync(id)));
};

/** Éxito, o error de negocio que cambia lo que hay que pintar (6100 ya resuelta, 6101 no existe). */
export const resolveOutcomeChangesNote = (data: any): boolean =>
  data?.success === true || data?.errorCode === AI_NOTE_ALREADY_RESOLVED || data?.errorCode === AI_NOTE_NOT_FOUND;

export const acceptAiNoteAsync: any = createAsyncThunk(
  'aiNotes/accept',
  async (data: { eventId: string; note?: string | null; expedientId?: string | null }, { dispatch, getState }) => {
    const response = await postAiNoteAccept(data.eventId, data.note);
    if (resolveOutcomeChangesNote(response?.data)) {
      // La nota aceptada ya es una NOTES de expediente (quien pinta las clásicas se refresca por su cuenta).
      refreshResolvedNote(dispatch, getState, data.eventId, data.expedientId);
    }
    return { data: response?.data };
  },
);

export const discardAiNoteAsync: any = createAsyncThunk(
  'aiNotes/discard',
  async (data: { eventId: string; expedientId?: string | null }, { dispatch, getState }) => {
    const response = await postAiNoteDiscard(data.eventId);
    if (resolveOutcomeChangesNote(response?.data)) {
      refreshResolvedNote(dispatch, getState, data.eventId, data.expedientId);
    }
    return { data: response?.data };
  },
);
